import { useCallback, useEffect, useRef, useState } from 'react';
import { useTargetsOnMap, BULK_TRACKS_MAX } from '../TargetsOnMapContext';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useFetchVectorTrack } from '@ais-ui/api';
import { formatVesselName } from '@ais-ui/utils';
import {
  useLayers,
  useOpenLayersContext,
  trackPathToVectorLayer,
  styleTrackSource,
  getTrackDisplayToggles,
  initLayerItem,
  baseColours,
  LayerItem,
} from '@ais-ui/react-openlayers';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import { onTrackTimeChange } from './trackEvents';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isStationaryTrack } from '../ContextMenu/isStationaryTrack';
import type { SogStop } from '@ais-ui/synthetic-geo';

export const GROUP_ID = 'targets-on-map-tracks';
const DEFAULT_HOURS = 4;
const REFRESH_INTERVAL_MS = 15 * 1000; // 15 seconds

interface Vessel {
  mmsi: number;
  name: string;
}

// ─── Pure helpers ────────────────────────────────────────────────────────────

function isValidDateRange(
  start: Date | undefined,
  end: Date | undefined,
): boolean {
  return !!start && !!end && !isNaN(start.getTime()) && !isNaN(end.getTime());
}

function formatDuration(start: Date, end: Date): string {
  const hours = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60),
  );
  return hours >= 24 ? `${Math.round(hours / 24)}d` : `${hours}h`;
}

function buildTrackMetadata(
  start: Date | undefined,
  end: Date | undefined,
  updatedAt: string,
): string {
  if (!start || !end || !isValidDateRange(start, end)) return updatedAt;
  return `${formatDuration(start, end)} • ${updatedAt}`;
}

function buildGroupMetadata(
  vesselCount: number,
  start: Date | undefined,
  end: Date | undefined,
  updatedAt: string,
): string {
  const base = `${vesselCount} vessels`;
  if (!start || !end || !isValidDateRange(start, end))
    return `${base} • ${updatedAt}`;
  return `${base} • ${formatDuration(start, end)} • ${updatedAt}`;
}

function getTrackStyleOptions(
  layer: LayerItem,
  displayProjection: string,
  dataProjection: string,
  formatTimestamp?: (isoTimestamp: string) => string,
) {
  return {
    color: layer.color || layer.properties?.defaultColor,
    colorBy: layer.properties?.colorBy as string | undefined,
    customSogStops: layer.properties?.customSogStops as SogStop[] | undefined,
    label: layer.name,
    ...getTrackDisplayToggles(layer.properties),
    formatTimestamp,
    displayProjection,
    dataProjection,
  };
}

function getLayerSource(layer: LayerItem) {
  const olLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
  return olLayer.getSource() as VectorSource<Feature<OlGeometry>> | null;
}

function parseTrackHours(layer: LayerItem): number {
  return (layer.properties?.trackHours as number) ?? DEFAULT_HOURS;
}

function isIndividualTrack(layer: LayerItem): boolean {
  return (
    layer.properties?.type === 'vector-track' &&
    !layer.id.startsWith(`${GROUP_ID}-`)
  );
}

// ─── useFetchAndCreateTracks ─────────────────────────────────────────────────

function useFetchAndCreateTracks() {
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const fetchVectorTrack = useFetchVectorTrack();

  return useCallback(
    async (vessels: Vessel[], startTime: Date, endTime: Date) => {
      const colourKeys = Object.keys(baseColours) as Array<
        keyof typeof baseColours
      >;

      const emptyVessels: string[] = [];
      const stationaryVessels: string[] = [];

      const results = await Promise.allSettled(
        vessels.map(async (vessel, index) => {
          const result = await fetchVectorTrack({
            mmsi: vessel.mmsi,
            startTime,
            endTime,
          });
          const track = result.tracks[0];
          if (!track?.path) {
            emptyVessels.push(formatVesselName(vessel.name));
            return null;
          }

          if (isStationaryTrack(track)) {
            stationaryVessels.push(formatVesselName(vessel.name));
          }

          const color = baseColours[colourKeys[index % colourKeys.length]];
          const vectorLayer = trackPathToVectorLayer(
            track,
            displayProjection,
            dataProjection,
            { color, label: formatVesselName(vessel.name) },
          );

          const trackItem = initLayerItem(
            vectorLayer,
            formatVesselName(vessel.name),
            false,
            true,
            true,
            false,
            {
              type: 'vector-track',
              track,
              defaultColor: color,
              layerMetadata: String(vessel.mmsi),
            },
            `${GROUP_ID}-track-${vessel.mmsi}`,
          );
          trackItem.color = color;
          return trackItem;
        }),
      );

      const fulfilled = results.filter(
        (r): r is PromiseFulfilledResult<LayerItem> =>
          r.status === 'fulfilled' && r.value != null,
      );
      return {
        items: fulfilled.map((r) => r.value),
        emptyVessels,
        stationaryVessels,
      };
    },
    [fetchVectorTrack, displayProjection, dataProjection],
  );
}

// ─── useShowAllTracks ────────────────────────────────────────────────────────

/**
 * Hook that fetches vector tracks for all visible targets and adds them
 * as a feature collection (nested layer group) to the layer control.
 */
export function useShowAllTracks() {
  const { targets } = useTargetsOnMap();
  const { addLayerWithChildren, doesLayerExist, removeLayerWithChildren } =
    useLayers();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const { formatTimeWithTimeZone } = useDateTimeFormat();
  const [isSelectingBulkTracks, setIsSelectingBulkTracks] = useState(false);
  const fetchAndCreateTracks = useFetchAndCreateTracks();

  const isBulkTracksActive = doesLayerExist(GROUP_ID);

  const activateBulkTracks = useCallback(
    async (overrideTargets?: typeof targets) => {
      if (doesLayerExist(GROUP_ID)) return;

      const vesselsToSelect = (overrideTargets ?? targets).slice(
        0,
        BULK_TRACKS_MAX,
      );
      if (vesselsToSelect.length === 0) {
        toast({
          title: messages.genericToastTitleWarning,
          description: 'No targets available to show tracks for',
          variant: 'warning',
        });
        return;
      }

      const vessels: Vessel[] = vesselsToSelect
        .map((t) => ({
          mmsi: t.mmsi ? Number(t.mmsi) : null,
          name: t.name || t.mmsi || 'Unknown',
        }))
        .filter((v): v is Vessel => v.mmsi !== null);

      if (vessels.length === 0) {
        toast({
          title: messages.genericToastTitleWarning,
          description: 'No valid MMSIs found in targets',
          variant: 'warning',
        });
        return;
      }

      setIsSelectingBulkTracks(true);

      const endTime = new Date();
      const startTime = new Date(
        endTime.getTime() - DEFAULT_HOURS * 60 * 60 * 1000,
      );

      const {
        items: childLayerItems,
        emptyVessels,
        stationaryVessels,
      } = await fetchAndCreateTracks(vessels, startTime, endTime);

      setIsSelectingBulkTracks(false);

      if (childLayerItems.length === 0) {
        toast({
          title: messages.genericToastTitleWarning,
          description: 'No tracks found for the selected vessels',
          variant: 'warning',
        });
        return;
      }

      const updatedAt = formatTimeWithTimeZone(new Date(), true);

      addLayerWithChildren(
        {
          id: GROUP_ID,
          name: 'Targets on map tracks',
          metadata: buildGroupMetadata(
            childLayerItems.length,
            startTime,
            endTime,
            updatedAt,
          ),
          isBase: false,
          isVisible: true,
          isRemovableFromLayerControl: true,
          canZoomToFromLayerControl: true,
          canShowLabel: false,
          properties: {
            type: 'featurecollection',
            timeControl: true,
            timeControlHours: DEFAULT_HOURS,
            vessels,
            lastUpdatedAt: new Date().toISOString(),
          },
          type: 'featurecollection',
        },
        childLayerItems,
      );

      const noteParts: string[] = [];
      if (stationaryVessels.length > 0) {
        noteParts.push(
          `${stationaryVessels.length} stationary (SOG 0 throughout)`,
        );
      }
      if (emptyVessels.length > 0) {
        noteParts.push(`${emptyVessels.length} with no track data`);
      }

      toast({
        title: 'Tracks activated',
        description:
          `Showing tracks for ${childLayerItems.length} vessels` +
          (noteParts.length > 0 ? ` — ${noteParts.join(', ')}.` : ''),
        variant: 'info',
      });
    },
    [
      targets,
      doesLayerExist,
      addLayerWithChildren,
      fetchAndCreateTracks,
      toast,
      messages,
      formatTimeWithTimeZone,
    ],
  );

  const deactivateBulkTracks = useCallback(() => {
    removeLayerWithChildren(GROUP_ID);
  }, [removeLayerWithChildren]);

  const toggleBulkTracks = useCallback(
    (overrideTargets?: typeof targets) => {
      if (isBulkTracksActive) {
        deactivateBulkTracks();
      } else {
        activateBulkTracks(overrideTargets);
      }
    },
    [isBulkTracksActive, activateBulkTracks, deactivateBulkTracks],
  );

  const canActivate = targets.length > 0 && targets.length <= BULK_TRACKS_MAX;
  const targetCount = Math.min(targets.length, BULK_TRACKS_MAX);

  return {
    activateBulkTracks,
    deactivateBulkTracks,
    toggleBulkTracks,
    isBulkTracksActive,
    isSelectingBulkTracks,
    canActivate,
    targetCount,
  };
}

// ─── useTrackTimeChangeListener ──────────────────────────────────────────────

/**
 * Listens for time range change events (from TrackTimeControl) and refreshes
 * track layers in-place — no remove/re-add flicker.
 */
export function useTrackTimeChangeListener() {
  const { layers, patchLayer } = useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const fetchVectorTrack = useFetchVectorTrack();
  const { formatTimeWithTimeZone } = useDateTimeFormat();
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentRangeRef = useRef<{ hours: number; isRelative: boolean }>({
    hours: DEFAULT_HOURS,
    isRelative: true,
  });

  const layersRef = useRef(layers);
  layersRef.current = layers;

  const formatTrackTimestamp = useCallback(
    (iso: string) => formatTimeWithTimeZone(new Date(iso), true),
    [formatTimeWithTimeZone],
  );

  /** Refresh a single track layer's source with new data. */
  const refreshSingleTrack = useCallback(
    async (layer: LayerItem, startTime: Date, endTime: Date) => {
      const mmsi = Number(layer.properties?.layerMetadata);
      if (!mmsi) return;

      const result = await fetchVectorTrack({ mmsi, startTime, endTime });
      const track = result.tracks[0];
      if (!track?.path) return;

      // Re-read fresh layer state after async gap to avoid overwriting user changes
      const freshLayer =
        layersRef.current.find((l) => l.id === layer.id) ??
        layersRef.current
          .flatMap((l) => l.children ?? [])
          .find((c) => c.id === layer.id);
      if (!freshLayer) return;

      const source = getLayerSource(freshLayer);
      if (!source) return;

      styleTrackSource(
        source,
        track,
        getTrackStyleOptions(
          freshLayer,
          displayProjection,
          dataProjection,
          formatTrackTimestamp,
        ),
      );

      const updatedAt = formatTimeWithTimeZone(new Date(), true);
      patchLayer(layer.id, {
        properties: { track, lastUpdatedAt: new Date().toISOString() },
        metadata: buildTrackMetadata(startTime, endTime, updatedAt),
      });
    },
    [
      fetchVectorTrack,
      displayProjection,
      dataProjection,
      patchLayer,
      formatTimeWithTimeZone,
      formatTrackTimestamp,
    ],
  );

  /** Refresh all children of the bulk tracks group. */
  const refreshBulkTracks = useCallback(
    async (startTime: Date, endTime: Date) => {
      if (!isValidDateRange(startTime, endTime)) return;

      const parent = layersRef.current.find((l) => l.id === GROUP_ID);
      if (!parent?.children) return;

      await Promise.allSettled(
        parent.children.map((child) =>
          refreshSingleTrack(child, startTime, endTime),
        ),
      );

      const diffH = Math.round(
        (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60),
      );
      const updatedAt = formatTimeWithTimeZone(new Date(), true);

      patchLayer(GROUP_ID, {
        metadata: buildGroupMetadata(
          parent.children.length,
          startTime,
          endTime,
          updatedAt,
        ),
        properties: {
          timeControlHours: diffH,
          lastUpdatedAt: new Date().toISOString(),
        },
      });
    },
    [refreshSingleTrack, patchLayer, formatTimeWithTimeZone],
  );

  /** Refresh individual (non-grouped) vector-track layers. Sliding window: end = now. */
  const refreshIndividualTracks = useCallback(async () => {
    const tracks = layersRef.current.filter(
      (l) => isIndividualTrack(l) && l.properties?.isRelativeRange !== false,
    );
    if (tracks.length === 0) return;

    await Promise.allSettled(
      tracks.map((layer) => {
        const hours = parseTrackHours(layer);
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - hours * 60 * 60 * 1000);
        return refreshSingleTrack(layer, startTime, endTime);
      }),
    );
  }, [refreshSingleTrack]);

  // Listen for explicit time range changes (user picks a new range)
  useEffect(() => {
    const unsubscribe = onTrackTimeChange(
      async ({ layerId, startTime, endTime }) => {
        if (!isValidDateRange(startTime, endTime)) return;

        if (layerId === GROUP_ID) {
          const diffH = Math.round(
            (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60),
          );
          const isRelative = Math.abs(endTime.getTime() - Date.now()) < 60_000;
          currentRangeRef.current = { hours: diffH, isRelative };
          await refreshBulkTracks(startTime, endTime);
          return;
        }

        const layer = layersRef.current.find((l) => l.id === layerId);
        if (!layer || layer.properties?.type !== 'vector-track') return;

        const hours = Math.round(
          (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60),
        );
        const isRelative = Math.abs(endTime.getTime() - Date.now()) < 60_000;
        patchLayer(layerId, {
          properties: { trackHours: hours, isRelativeRange: isRelative },
        });
        await refreshSingleTrack(layer, startTime, endTime);
      },
    );

    return unsubscribe;
  }, [refreshBulkTracks, refreshSingleTrack, patchLayer]);

  // Stable refs for interval callbacks — avoids resetting the timer on every render
  const refreshBulkTracksRef = useRef(refreshBulkTracks);
  refreshBulkTracksRef.current = refreshBulkTracks;
  const refreshIndividualTracksRef = useRef(refreshIndividualTracks);
  refreshIndividualTracksRef.current = refreshIndividualTracks;

  // Auto-refresh on a short interval to keep tracks current
  useEffect(() => {
    const hasTracksToRefresh = () => {
      const current = layersRef.current;
      return {
        bulk: current.some((l) => l.id === GROUP_ID),
        individual: current.some(isIndividualTrack),
      };
    };

    refreshTimerRef.current = setInterval(() => {
      const { bulk, individual } = hasTracksToRefresh();
      if (bulk && currentRangeRef.current.isRelative) {
        const hours = currentRangeRef.current.hours || DEFAULT_HOURS;
        const now = new Date();
        const start = new Date(now.getTime() - hours * 60 * 60 * 1000);
        refreshBulkTracksRef.current(start, now);
      }
      if (individual) {
        refreshIndividualTracksRef.current();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);
}
